import { useState, useEffect, useRef, useCallback } from "react";
import { DataSource } from "@/config/datasources";
import { useNewConversationFiltersStore } from "@/stores/new-conversation-filters.store";
import { useSourceSelection } from "@/pages/chat/hooks/useSourceSelection";
import useLegislationFilters from "@/hooks/useLegislationFilters";
import { Option } from "../components/prompt-library/PromptLibraryListView";

type UseSourceSelectionDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const useSourceSelectionDialog = ({
  open,
  onOpenChange,
}: UseSourceSelectionDialogProps) => {
  const {
    setFilters,
    removeFilter,
    workspaceContext,
    filters,
    setSelectedSource,
  } = useNewConversationFiltersStore();
  const sourceSelection = useSourceSelection();
  const { getSelectedSource, getFilterValues } = sourceSelection;
  const { useGetFilterConfiguration } = useLegislationFilters();
  const fetchedFilterConfiguration = useGetFilterConfiguration();

  const [availableSources, setAvailableSources] = useState<Option[]>([]);
  const [localSelectedSource, setLocalSelectedSource] = useState<
    Option | undefined
  >(undefined);

  const [filterValues, setFilterValues] = useState<Record<string, unknown>>({});
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);

  const sourceListRef = useRef<HTMLDivElement>(null);
  const isSyncingRef = useRef<boolean>(false);
  const lastSyncedFiltersRef = useRef<string>("");
  const hasInitialFocusRef = useRef<boolean>(false);

  // Load available sources from filter configuration
  useEffect(() => {
    if (
      fetchedFilterConfiguration.isFetched &&
      fetchedFilterConfiguration.data
    ) {
      const sources = Object.entries(fetchedFilterConfiguration.data.sources);
      // Filter out Workspace source and map to Option[]
      const filteredSources = sources
        .filter(([sourceName]) => sourceName !== DataSource.Workspace)
        .map(([sourceName, sourceDef]) => ({
          label: sourceDef.displayName,
          value: sourceName,
        }));
      // Sort alphabetically by displayName
      const sortedSources = filteredSources.sort((a, b) =>
        a.label.localeCompare(b.label, "da", { sensitivity: "base" })
      );
      setAvailableSources(sortedSources);
      setHighlightedIndex(-1);
    }
  }, [
    fetchedFilterConfiguration.isFetched,
    fetchedFilterConfiguration.data,
    open,
  ]);

  // Sync with store's filters when dialog opens or filters change
  useEffect(() => {
    if (!open) {
      // Reset when dialog closes
      setLocalSelectedSource(undefined);
      setFilterValues({});
      setHighlightedIndex(-1);
      isSyncingRef.current = false;
      lastSyncedFiltersRef.current = "";
      hasInitialFocusRef.current = false;
      return;
    }

    // Don't sync if we're currently syncing or don't have sources yet
    if (isSyncingRef.current || availableSources.length === 0) {
      return;
    }

    // Check if filters changed
    const filtersStr = JSON.stringify(filters);
    if (filtersStr === lastSyncedFiltersRef.current) {
      return; // No changes
    }

    // Mark as syncing to prevent loops
    isSyncingRef.current = true;
    lastSyncedFiltersRef.current = filtersStr;

    // Get source and filters from store
    const sourceValues = availableSources.map(
      (s) => s.value as DataSource
    );
    const storeSourceValue = getSelectedSource(sourceValues);
    const storeSource = storeSourceValue
      ? availableSources.find((s) => s.value === storeSourceValue)
      : undefined;
    const storeFilterValues = storeSourceValue
      ? getFilterValues(storeSourceValue)
      : undefined;

    // Update local state if store state differs
    const sourceChanged = storeSource?.value !== localSelectedSource?.value;
    const currentFilterValuesStr = JSON.stringify(filterValues);
    const storeFilterValuesStr = JSON.stringify(storeFilterValues || {});
    const filterValuesChanged = currentFilterValuesStr !== storeFilterValuesStr;

    if (sourceChanged) {
      setLocalSelectedSource(storeSource);
      setFilterValues(storeFilterValues || {});
    } else if (filterValuesChanged && storeSource) {
      setFilterValues(storeFilterValues || {});
    }

    // Reset syncing flag after state updates
    setTimeout(() => {
      isSyncingRef.current = false;
    }, 0);

    // Focus the listbox on initial open
    if (!hasInitialFocusRef.current) {
      hasInitialFocusRef.current = true;
      setTimeout(() => {
        sourceListRef.current?.focus();
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, filters, availableSources]);

  const loadSourceConfiguration = useCallback((source: Option) => {
    // TODO: Apply metadata filtering when it is created on sources again
    setLocalSelectedSource(source);
    setFilterValues({});
  }, []);

  const handleSourceRowClick = useCallback(
    (source: Option) => {
      if (localSelectedSource?.value === source.value) {
        // Deselect if clicking on already selected source
        setLocalSelectedSource(undefined);
        setFilterValues({});
      } else {
        // Select new source
        loadSourceConfiguration(source);
      }
    },
    [localSelectedSource, loadSourceConfiguration]
  );

  const handleCheckboxChange = useCallback(
    (source: Option, checked: boolean) => {
      if (checked) {
        loadSourceConfiguration(source);
      } else {
        setLocalSelectedSource(undefined);
        setFilterValues({});
      }
    },
    [loadSourceConfiguration]
  );

  const handleCreateFilters = useCallback(() => {
    if (!localSelectedSource) return;

    // Remove any existing source filters before adding the new one
    const sourceValues = availableSources.map(
      (s) => s.value as DataSource
    );
    const existingSourceValue = getSelectedSource(sourceValues);
    if (existingSourceValue) {
      const existingFilters = filters[existingSourceValue] || [];
      // Remove filters from the end to avoid index shifting issues
      for (let i = existingFilters.length - 1; i >= 0; i--) {
        removeFilter(existingSourceValue, i);
      }
    }

    // Determine the filter values to apply
    // TODO: Re-enable when metadata filtering is restored
    let valuesToApply: Record<string, unknown> = {};
    if (Object.keys(filterValues).length > 0) {
      valuesToApply = filterValues;
    } else {
      // TODO: Create a filter with default values when no filter values are provided
      valuesToApply = {};
    }

    // Add the new filter (use value for the source name)
    const sourceValue = localSelectedSource.value as DataSource;
    setFilters(sourceValue, valuesToApply);
    // Also update the selectedSource in the store so it's available for message sending
    setSelectedSource(sourceValue);

    // Reset local state and close dialog
    setLocalSelectedSource(undefined);
    setFilterValues({});
    onOpenChange(false);
  }, [
    localSelectedSource,
    filterValues,
    availableSources,
    setFilters,
    removeFilter,
    getSelectedSource,
    filters,
    onOpenChange,
    setSelectedSource,
  ]);

  const handleSourceListKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (availableSources.length === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedIndex((prev) => {
            const next = prev < availableSources.length - 1 ? prev + 1 : 0;
            return next;
          });
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex((prev) => {
            const next = prev > 0 ? prev - 1 : availableSources.length - 1;
            return next;
          });
          break;
        case "Enter":
          e.preventDefault();
          if (
            highlightedIndex >= 0 &&
            highlightedIndex < availableSources.length
          ) {
            const source = availableSources[highlightedIndex];
            handleSourceRowClick(source);
          }
          break;
        case "Escape":
          if (localSelectedSource) {
            e.preventDefault();
            setLocalSelectedSource(undefined);
            setFilterValues({});
            setHighlightedIndex(-1);
          }
          break;
      }
    },
    [
      availableSources,
      highlightedIndex,
      localSelectedSource,
      handleSourceRowClick,
    ]
  );

  const hasWorkspaceContext = Object.keys(workspaceContext).length > 0;

  return {
    availableSources,
    selectedSource: localSelectedSource,
    filterValues,
    highlightedIndex,
    sourceListRef,
    hasWorkspaceContext,
    handleSourceRowClick,
    handleCheckboxChange,
    handleCreateFilters,
    handleSourceListKeyDown,
    /*  handleFilterChange,
    handleFilterClear, */
    setSelectedSource: setLocalSelectedSource,
    setFilterValues,
    setHighlightedIndex,
  };
};
